<script lang="ts" setup>
import projectStore from '@/store/ProjectStore'
import { ValidatorType } from '@/helper/ValidateDirective'
import { reactive } from 'vue'
import { ExistingBackupHeaterType } from '@/helper/Types'
import formStore from '@/store/FormStore'
import FormElement from '@/components/FormElement.vue'
import InputSuffix from '@/components/InputSuffix.vue'
import { StepsController } from '@/controllers/StepsController'
import userStore from '@/store/UserStore.ts'

const props = defineProps<{
  validator: ValidatorType
}>()

const existingBackupHeater = reactive<ExistingBackupHeaterType>({
  name: (formStore.data.existingBackupHeater as ExistingBackupHeaterType).name || '',
  powerOutput: (formStore.data.existingBackupHeater as ExistingBackupHeaterType).powerOutput || '',
  ebus: (formStore.data.existingBackupHeater as ExistingBackupHeaterType).ebus || false,
  dhw: (formStore.data.existingBackupHeater as ExistingBackupHeaterType).dhw || userStore.preselectExistingBackupHeaterCombiBoiler,
})

function addExistingBackupHeater() {
  formStore.data.existingBackupHeater = existingBackupHeater

  StepsController.currentStepValid(false)
}

defineExpose({
  validator: props.validator
})
</script>

<template>
  <div id="existing-backup-heater" class="steps-table__excluded-info excluded-info card">
    <div class="col-md-4">
      <FormElement
        v-validate="projectStore.projectProperties.existingBackupHeater.formValidationRule.name"
        :error="validator.existingBackupHeaterName?.error"
        :label="$t('step_components.existing_backup_heater_name')"
        :required="true"
      >
        <input
          id="existing-backup-heater-name"
          v-model="existingBackupHeater.name"
          :placeholder="$t('step_components.existing_backup_heater_name_placeholder')"
          data-hj-suppress
          name="existing-backup-heater-name"
          type="text"
          @input="addExistingBackupHeater"
        >
      </FormElement>
    </div>

    <div class="col-md-4">
      <FormElement
        v-validate="projectStore.projectProperties.existingBackupHeater.formValidationRule.powerOutput"
        :error="validator.existingBackupHeaterPowerOutput?.error"
        :label="$t('step_components.existing_backup_heater_heating_output')"
      >
        <InputSuffix
          id="existing-backup-heater-power-output"
          v-model="existingBackupHeater.powerOutput"
          :placeholder="$t('step_components.existing_backup_heater_heating_output_placeholder')"
          suffix=" kW"
          @input="addExistingBackupHeater"
        />
      </FormElement>
    </div>

    <div class="col-md-4">
      <FormElement
        v-validate="projectStore.projectProperties.existingBackupHeater.formValidationRule.ebus"
        :error="validator.existingBackupHeater?.error"
        :label="$t('step_components.existing_backup_heater_other')"
      >
        <label class="form__checkbox">
          <input
            id="existing-backupheater-ebus"
            v-model="existingBackupHeater.ebus"
            type="checkbox"
            @change="addExistingBackupHeater"
          >
          <span class="form__cm is--large" />
          {{ $t('step_components.existing_backup_heater_ebus') }}
        </label>
      </FormElement>
      <FormElement
        v-validate="projectStore.projectProperties.existingBackupHeater.formValidationRule.dhw"
        :error="validator.existingBackupHeater?.error"
      >
        <label class="form__checkbox">
          <input
            id="existing-backupheater-dhw"
            v-model="existingBackupHeater.dhw"
            type="checkbox"
            @change="addExistingBackupHeater"
          >
          <span class="form__cm is--large" />
          {{ $t('step_components.existing_backup_heater_dhw') }}
        </label>
      </FormElement>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/style/helpers/include-media';

#existing-backup-heater {
  gap: 1rem;

  @include media('<tablet') {
    gap: 0;
    flex-direction: column;
  }
}

.excluded-info {
  align-items: start;

  [class^='col-md-'] {
    padding-left: 0;
    padding-right: 0;
    flex: 1;
  }
}

.inputsuffix__wrapper {
  width: 100%;
}

:deep(.form__input-wrapper) {
  height: rem-calc(40);
}

label.form__checkbox {
  margin-bottom: 0;
}

.form__element {
  margin-bottom: 0;
  margin-top: 0;
}
</style>
